import { capitalize } from 'lodash-es'
import {
  BASE_TEMPLATES,
  CUSTOMER_TEMPLATES,
  GRAMMAR,
  type BaseModuleMessages,
  type CustomerMessages,
  type ModuleAliasInput,
} from '~/config/grammar.config'

/**
 * Type of messages to generate
 * - 'base': Only basic messages (meta.singular, meta.plural)
 * - 'customer': Full customer messages (all customer-specific texts)
 */
export type MessageType = 'base' | 'customer'

/**
 * Normalizes the alias input to ensure proper casing
 * API might return uppercase values like "ASISTENTE", this converts to "Asistente"
 *
 * @param input - Raw alias input from API
 * @returns Normalized input with proper casing
 */
export function normalizeAliasInput(input: ModuleAliasInput): ModuleAliasInput {
  return {
    s: capitalize(input.s.toLowerCase()),
    p: capitalize(input.p.toLowerCase()),
    g: input.g,
  }
}

/**
 * Builds the replacement map for template processing
 *
 * @param alias - Normalized module alias input
 * @returns Record of placeholder keys to their replacement values
 */
function buildReplacementMap(alias: ModuleAliasInput): Record<string, string> {
  const grammar = GRAMMAR[alias.g]

  return {
    // Singular and plural forms
    '{s}': alias.s.toLowerCase(),
    '{S}': alias.s,
    '{p}': alias.p.toLowerCase(),
    '{P}': alias.p,

    // Grammar variants (as-is from config)
    '{el}': grammar.el,
    '{los}': grammar.los,
    '{del}': grammar.del,
    '{al}': grammar.al,
    '{un}': grammar.un,
    '{este}': grammar.este,
    '{nuevo}': grammar.nuevo,
    '{todos}': grammar.todos,

    // Derived variants
    '{nuevo_lower}': grammar.nuevo.toLowerCase(),
  }
}

/**
 * Processes a single template string, replacing all placeholders with values
 *
 * @param template - Template string with placeholders like {s}, {nuevo}, etc.
 * @param replacements - Map of placeholder to replacement value
 * @returns Processed string with all placeholders replaced
 *
 * @example
 * ```ts
 * const result = processTemplate(
 *   '¿Deseas crear {un} {nuevo_lower} {s}?',
 *   { '{un}': 'una', '{nuevo_lower}': 'nueva', '{s}': 'persona' }
 * )
 * // Returns: "¿Deseas crear una nueva persona?"
 * ```
 */
export function processTemplate(
  template: string,
  replacements: Record<string, string>
): string {
  let result = template

  for (const [placeholder, value] of Object.entries(replacements)) {
    result = result.replaceAll(placeholder, value)
  }

  return result
}

/**
 * Recursively processes nested template objects
 *
 * @param templates - Object containing template strings or nested objects
 * @param replacements - Map of placeholder to replacement value
 * @returns Processed object with all template strings replaced
 */
function processTemplatesRecursive(
  templates: Record<string, unknown>,
  replacements: Record<string, string>
): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(templates)) {
    if (typeof value === 'string') {
      result[key] = processTemplate(value, replacements)
    } else if (typeof value === 'object' && value !== null) {
      result[key] = processTemplatesRecursive(
        value as Record<string, unknown>,
        replacements
      )
    } else {
      result[key] = value
    }
  }

  return result
}

/**
 * Generates base module messages (minimum required for all modules)
 *
 * @param rawInput - Module alias input from API (can be uppercase)
 * @returns BaseModuleMessages object with meta.singular and meta.plural
 *
 * @example
 * ```ts
 * const messages = generateBaseMessages({
 *   s: 'REPORTE',
 *   p: 'REPORTES',
 *   g: 'm'
 * })
 *
 * console.log(messages.meta.plural)   // "Reportes"
 * console.log(messages.meta.singular) // "Reporte"
 * ```
 */
export function generateBaseMessages(
  rawInput: ModuleAliasInput
): BaseModuleMessages {
  const alias = normalizeAliasInput(rawInput)
  const replacements = buildReplacementMap(alias)

  return processTemplatesRecursive(
    BASE_TEMPLATES as unknown as Record<string, unknown>,
    replacements
  ) as BaseModuleMessages
}

/**
 * Generates customer module messages (full set for customers module)
 *
 * Takes singular, plural and gender and produces all the text variations
 * needed for UI labels, confirmations, errors, etc.
 *
 * @param rawInput - Module alias input from API (can be uppercase)
 * @returns CustomerMessages object with all generated texts in hierarchical structure
 *
 * @example
 * ```ts
 * const messages = generateCustomerMessages({
 *   s: 'ASISTENTE',
 *   p: 'ASISTENTES',
 *   g: 'm'
 * })
 *
 * console.log(messages.meta.plural)        // "Asistentes"
 * console.log(messages.titles.new)         // "Nuevo Asistente"
 * console.log(messages.confirmations.new)  // "¿Deseas crear un nuevo asistente?"
 * ```
 */
export function generateCustomerMessages(
  rawInput: ModuleAliasInput
): CustomerMessages {
  const alias = normalizeAliasInput(rawInput)
  const replacements = buildReplacementMap(alias)

  return processTemplatesRecursive(
    CUSTOMER_TEMPLATES as unknown as Record<string, unknown>,
    replacements
  ) as CustomerMessages
}

/**
 * Default messages for Customers module (fallback when no alias configured)
 */
export const DEFAULT_CUSTOMER_MESSAGES: CustomerMessages =
  generateCustomerMessages({
    s: 'Cliente',
    p: 'Clientes',
    g: 'm',
  })

/**
 * Gets customer module messages, using alias if provided or falling back to defaults
 *
 * @param alias - Optional alias input from API
 * @param defaultMessages - Default messages to use if no alias
 * @returns CustomerMessages object
 */
export function getCustomerMessagesOrDefault(
  alias: ModuleAliasInput | undefined,
  defaultMessages: CustomerMessages
): CustomerMessages {
  if (!alias) {
    return defaultMessages
  }
  return generateCustomerMessages(alias)
}

/**
 * Gets base module messages, using alias if provided or falling back to defaults
 *
 * @param alias - Optional alias input from API
 * @param defaultMessages - Default messages to use if no alias
 * @returns BaseModuleMessages object
 */
export function getBaseMessagesOrDefault(
  alias: ModuleAliasInput | undefined,
  defaultMessages: BaseModuleMessages
): BaseModuleMessages {
  if (!alias) {
    return defaultMessages
  }
  return generateBaseMessages(alias)
}
